/**
 * <s-product-gallery> — PDP gallery with a thumbnail rail.
 *
 * Required markup:
 *   [data-gallery-stage]        — horizontal scroll-snap track holding the slides.
 *   [data-gallery-slide]        — one per media item, in the same order as the thumbnails.
 *   [data-gallery-thumb]        — thumbnail buttons inside a role="tablist".
 *
 * Thumbnails are a tablist with roving tabindex: arrows/Home/End move focus and select,
 * selecting scrolls the stage without moving focus, and swiping the stage syncs the rail.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.stage = this.querySelector('[data-gallery-stage]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.stage || this.thumbs.length === 0) return;

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.#select(index, true), opts);
      thumb.addEventListener('keydown', (event) => this.#onKeydown(event, index), opts);
    });

    // Keep the rail in sync when the buyer swipes or scrolls the stage directly.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.#setActive(this.slides.indexOf(entry.target));
          }
        }
      },
      { root: this.stage, threshold: [0.6] }
    );
    this.slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #onKeydown(event, index) {
    const last = this.thumbs.length - 1;
    let next = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next === null) return;
    event.preventDefault();
    this.thumbs[next].focus();
    this.#select(next, true);
  }

  #select(index, scroll) {
    this.#setActive(index);
    if (!scroll) return;
    const slide = this.slides[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // scrollIntoView respects RTL scroll direction; block: 'nearest' avoids jumping the page.
    slide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  #setActive(index) {
    if (index < 0 || index === this.#index && this.thumbs[index]?.getAttribute('aria-selected') === 'true') return;
    this.#index = index;
    this.thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.setAttribute('aria-selected', String(active));
      thumb.tabIndex = active ? 0 : -1;
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
